import React, { useEffect, useState } from 'react';
import { attachmentsApi } from '../services/resources';

/**
 * Shows an already-uploaded attachment (a photo, most of the time) inline —
 * a thumbnail that opens the full-size image in a new tab on click. Added
 * because an edit form's plain `<input type="file">` always reads "No file
 * chosen", even when the record already has one: there was previously no
 * way at all to see what an Operator had actually uploaded before approving
 * it (Milk Sessions, in particular, since approval depends on checking the
 * entered figures against this exact photo).
 *
 * Fetched as an authenticated blob (not a plain <img src="...">) because the
 * file-serving endpoint requires the same Bearer token as every other API
 * call — a bare <img>/<a> tag can't attach that header on its own.
 */
export function ILMSAttachmentPreview({ attachmentId, label = 'Current photo' }: { attachmentId: string; label?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(false);
    attachmentsApi
      .fetchFileBlob(attachmentId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentId]);

  if (error) {
    return <p className="text-helper-text text-critical-600">Unable to load the existing photo.</p>;
  }

  return (
    <div className="flex items-center gap-2.5">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" title="Open full size in a new tab">
          <img src={url} alt={label} className="h-16 w-16 rounded-md border border-ink-300/20 object-cover hover:opacity-90" />
        </a>
      ) : (
        <div className="h-16 w-16 rounded-md border border-ink-300/20 bg-surface-muted animate-pulse" />
      )}
      <div className="flex flex-col">
        <span className="text-secondary text-ink-700">{label}</span>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" className="text-helper-text text-brand-green-700 hover:underline">
            View full size ↗
          </a>
        )}
      </div>
    </div>
  );
}
